import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api, { resolveAssetUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, ChatCircleDots, Sparkle } from "@phosphor-icons/react";

export default function MyChatsPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    api.get("/chats")
      .then(({ data }) => setChats(data))
      .finally(() => setFetching(false));
  }, [user, loading, navigate]);

  // Sort: active chats first, then newest first
  const sorted = [...chats].sort((a, b) => {
    if (a.status === "active" && b.status !== "active") return -1;
    if (b.status === "active" && a.status !== "active") return 1;
    return new Date(b.started_at) - new Date(a.started_at);
  });

  const fmtRelative = (iso) => {
    if (!iso) return "";
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  };

  return (
    <div className="bg-[#FAF4E8] md:bg-transparent min-h-[100dvh]" data-testid="my-chats-page">
      {/* Desktop site header; mobile uses its own compact app-bar */}
      <div className="hidden md:block">
        <Header />
      </div>

      <div className="md:max-w-3xl md:mx-auto md:px-6 lg:px-10 md:pt-8 md:pb-12">
        {/* Mobile app-bar */}
        <div className="md:hidden flex items-center gap-3 px-3 py-3 border-b border-soft bg-[#FAF4E8] sticky top-0 z-10 pt-[max(env(safe-area-inset-top),0.75rem)]">
          <button
            onClick={() => navigate("/")}
            className="p-1.5 -ml-1 rounded-full hover:bg-black/5 transition-colors flex-shrink-0"
            aria-label="Back"
            data-testid="my-chats-back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-serif-display text-xl flex-1 truncate">My chats</h1>
          <Link
            to="/astrologers"
            className="btn-primary rounded-full px-3.5 py-1.5 text-xs font-medium flex items-center gap-1.5 flex-shrink-0"
            data-testid="new-chat-btn"
          >
            <Sparkle size={12} weight="fill" /> New
          </Link>
        </div>

        {/* Desktop header */}
        <div className="hidden md:flex items-end justify-between gap-4 mb-6">
          <div>
            <p className="label-eyebrow">Conversation history</p>
            <h2 className="font-serif-display text-4xl mt-2 font-light">My chats</h2>
          </div>
          <Link
            to="/astrologers"
            className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2"
            data-testid="new-chat-btn-desktop"
          >
            <Sparkle size={14} weight="fill" /> New consultation
          </Link>
        </div>

        {/* Chats list */}
        <div className="px-2 sm:px-0 py-2 sm:py-0">
          {fetching ? (
            <div className="text-muted text-sm py-12 text-center">Loading your chats...</div>
          ) : sorted.length === 0 ? (
            <div className="card-surface md:rounded-md p-8 sm:p-12 text-center mx-2 md:mx-0 mt-4 md:mt-0" data-testid="my-chats-empty">
              <ChatCircleDots size={40} weight="duotone" className="text-gold opacity-70 mx-auto" />
              <p className="font-serif-display text-2xl sm:text-3xl mt-3 font-light">No conversations yet</p>
              <p className="text-muted text-sm mt-2 max-w-md mx-auto">
                Start a reading with an astrologer — your first 2 minutes with any AI guide are on us.
              </p>
              <Link
                to="/astrologers"
                className="btn-primary rounded-full inline-flex items-center gap-2 px-6 py-3 font-medium mt-5"
                data-testid="empty-browse-cta"
              >
                <Sparkle size={14} weight="fill" /> Browse astrologers
              </Link>
            </div>
          ) : (
            <div className="md:space-y-3 divide-y divide-soft md:divide-y-0">
              {sorted.map((c) => {
                const active = c.status === "active";
                return (
                  <Link
                    to={`/chat/${c.chat_id}`}
                    key={c.chat_id}
                    className="flex items-center gap-3 sm:gap-4 px-2 sm:px-5 py-3.5 sm:py-4 md:card-surface md:rounded-md hover:bg-[#FFF6E0]/40 transition-colors"
                    data-testid={`my-chats-row-${c.chat_id}`}
                  >
                    <img
                      src={resolveAssetUrl(c.astrologer_avatar)}
                      alt=""
                      className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-serif-display text-base sm:text-lg truncate">{c.astrologer_name}</div>
                        {active && (
                          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse flex-shrink-0" title="Active session" />
                        )}
                      </div>
                      <div className="text-muted text-xs mt-0.5 truncate">
                        {active ? "Tap to resume · " : ""}{fmtRelative(c.started_at)}
                      </div>
                    </div>
                    <span
                      className={`label-eyebrow !text-[10px] sm:!text-xs px-2 sm:px-3 py-1 rounded-full border flex-shrink-0 ${
                        active
                          ? "border-green-500/40 text-green-700 bg-green-50"
                          : "border-soft text-muted"
                      }`}
                    >
                      {c.status}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="hidden md:block">
        <Footer />
      </div>
    </div>
  );
}
